import type { AiSkillSettings, TenantMemberSummary } from '@livechat/shared';
import { useEffect, useState } from 'react';
import {
	createTenantAiSkillVersion,
	getTenantAiSkills,
	listMembers,
	saveTenantAiSkillAssignments,
	setTenantAiSkillLiveExecution,
} from '../api.js';

export function TenantAiAssignments({
	tenantId,
	onDraftChange,
}: {
	tenantId: string;
	onDraftChange: () => void;
}) {
	const [settings, setSettings] = useState<AiSkillSettings>();
	const [members, setMembers] = useState<TenantMemberSummary[]>([]);
	const [assignments, setAssignments] = useState<AiSkillSettings['assignments']>([]);
	const [busy, setBusy] = useState(false);
	const [dirty, setDirty] = useState(false);
	const [error, setError] = useState<string>();
	const [message, setMessage] = useState<string>();
	useEffect(() => {
		let current = true;
		setSettings(undefined);
		setError(undefined);
		void Promise.all([getTenantAiSkills(tenantId), listMembers(tenantId)])
			.then(([loaded, team]) => {
				if (!current) return;
				setSettings(loaded);
				setAssignments(loaded.assignments);
				setMembers(team.filter((member) => member.memberType === 'ai_agent'));
			})
			.catch((caught: unknown) => {
				if (current)
					setError(
						caught instanceof Error
							? caught.message
							: 'Could not load action assignments',
					);
			});
		return () => {
			current = false;
		};
	}, [tenantId]);

	async function mutate(operation: () => Promise<AiSkillSettings>, publishingDraft: boolean) {
		if (busy) return;
		setBusy(true);
		setError(undefined);
		setMessage(undefined);
		try {
			const updated = await operation();
			setSettings(updated);
			if (publishingDraft) {
				setAssignments(updated.assignments);
				setDirty(false);
				onDraftChange();
			}
			setMessage(
				publishingDraft
					? 'Draft saved. Publish from Overview & Publish when ready. Live calls require platform availability and company opt-in.'
					: 'Immutable version saved. Select it below to update an agent’s Draft assignment.',
			);
		} catch (caught) {
			setError(
				caught instanceof Error ? caught.message : 'Could not save action configuration',
			);
		} finally {
			setBusy(false);
		}
	}

	return (
		<section className="settings-card integration-panel">
			<div className="integration-panel-heading">
				<h4 className="panel-title">Agent Actions</h4>
				<p>
					Save a frozen action version, assign it to an AI agent, then publish the company
					Draft. Editing an API Skill never changes an existing version.
				</p>
			</div>
			<div className="ai-skills-foundation-notice">
				<strong>
					{settings?.liveExecutionAvailable
						? 'Controlled read-only lookups'
						: 'Preparation only'}
				</strong>
				<span>
					Live AI calls require platform availability and a Published company opt-in.
					Copilot calls remain disabled. Credentials are never included in versions;
					disabling a connection, action or credential prevents publication or restore.
				</span>
			</div>
			{error && (
				<div className="alert error" role="alert">
					{error}
				</div>
			)}
			{message && (
				<div className="alert success" role="status">
					{message}
				</div>
			)}
			{!settings ? (
				<p>Loading action versions…</p>
			) : (
				<>
					<label className="switch-row">
						<div className="settings-option-copy">
							<span className="settings-option-title">Live read-only lookups</span>
							<span className="settings-option-description">
								{settings.liveExecutionAvailable
									? 'Enable in Draft, then Publish for new AI conversations. Turning off stops further calls immediately, including pinned conversations.'
									: 'Awaiting controlled platform acceptance. You can still configure and test Draft actions.'}
							</span>
						</div>
						<input
							type="checkbox"
							checked={settings.liveExecutionEnabled}
							disabled={
								busy ||
								dirty ||
								(!settings.liveExecutionAvailable && !settings.liveExecutionEnabled)
							}
							onChange={(event) => {
								const enabled = event.currentTarget.checked;
								void mutate(
									() => setTenantAiSkillLiveExecution(tenantId, enabled),
									true,
								);
							}}
						/>
					</label>
					{settings.actions.length === 0 && (
						<p>Create and test a read-only action in API Skills first.</p>
					)}
					{settings.actions.map((action) => (
						<section key={action.id} className="settings-card integration-panel">
							<div className="integration-panel-heading">
								<h4 className="panel-title">{action.name}</h4>
								<p>{action.description}</p>
							</div>
							<button
								type="button"
								className="secondary-button"
								disabled={busy || action.status !== 'draft'}
								onClick={() =>
									void mutate(
										() => createTenantAiSkillVersion(tenantId, action.id),
										false,
									)
								}
							>
								Save new version of {action.name}
							</button>
							{settings.versions
								.filter((version) => version.actionId === action.id)
								.map((version) => (
									<details key={version.id} className="ai-version-history">
										<summary>View saved version {version.version}</summary>
										<div className="code-block">
											<code>
												{JSON.stringify(version.definition, null, 2)}
											</code>
										</div>
									</details>
								))}
							{members.map((member) => (
								<label key={member.id} className="tenant-ai-strictness-setting">
									<span>{member.displayName}</span>
									<select
										aria-label={`${action.name} for ${member.displayName}`}
										disabled={busy}
										value={
											assignments.find(
												(entry) =>
													entry.agentUserId === member.id &&
													settings.versions.some(
														(version) =>
															version.actionId === action.id &&
															version.id === entry.actionVersionId,
													),
											)?.actionVersionId ?? ''
										}
										onChange={(event) => {
											const versionId = event.currentTarget.value;
											setAssignments((current) => [
												...current.filter(
													(entry) =>
														!(
															entry.agentUserId === member.id &&
															settings.versions.some(
																(version) =>
																	version.actionId ===
																		action.id &&
																	version.id ===
																		entry.actionVersionId,
															)
														),
												),
												...(versionId
													? [
															{
																agentUserId: member.id,
																actionVersionId: versionId,
															},
														]
													: []),
											]);
											setDirty(true);
											setMessage(undefined);
										}}
									>
										<option value="">Not assigned</option>
										{settings.versions
											.filter((version) => version.actionId === action.id)
											.map((version) => (
												<option value={version.id} key={version.id}>
													Version {version.version} ·{' '}
													{version.definition.method}{' '}
													{version.definition.baseOrigin}
													{version.definition.path}
												</option>
											))}
									</select>
								</label>
							))}
						</section>
					))}
					{members.length === 0 && (
						<p>Add an AI team member in Team before assigning actions.</p>
					)}
					{assignments.length > 0 && (
						<div className="integration-panel-heading">
							<h4 className="panel-title">Draft assignments</h4>
							<p>
								Remove an assignment here even if its action or agent is no longer
								available.
							</p>
							{assignments.map((entry) => {
								const version = settings.versions.find(
									(candidate) => candidate.id === entry.actionVersionId,
								);
								const name = `${members.find((member) => member.id === entry.agentUserId)?.displayName ?? 'Unavailable agent'} · ${version?.definition.name ?? 'Unavailable action'} · Version ${version?.version ?? '?'}`;
								return (
									<div
										key={`${entry.agentUserId}:${entry.actionVersionId}`}
										className="settings-option-copy"
									>
										<span>{name}</span>
										<button
											type="button"
											className="secondary-button"
											disabled={busy}
											aria-label={`Remove ${name}`}
											onClick={() => {
												setAssignments((current) =>
													current.filter(
														(candidate) => candidate !== entry,
													),
												);
												setDirty(true);
												setMessage(undefined);
											}}
										>
											Remove assignment
										</button>
									</div>
								);
							})}
						</div>
					)}
					<button
						type="button"
						className="primary-button"
						disabled={busy || !dirty}
						onClick={() =>
							void mutate(
								() => saveTenantAiSkillAssignments(tenantId, assignments),
								true,
							)
						}
					>
						Save Draft assignments
					</button>
				</>
			)}
		</section>
	);
}
